import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import api, { API } from "@/lib/api";
import { Bot, Send, Loader2, User, FileSignature, MessageSquare, Copy, Download, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const DOC_TYPES = [
  "Affidavit", "Declaration of Trust", "Security Agreement", "Power of Attorney",
  "Notice / Letter", "General Legal Document",
];

async function streamPost(path, body, onChunk) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) throw new Error("Request failed");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    onChunk(decoder.decode(value, { stream: true }));
  }
}

export default function Assistant() {
  const { user, refreshUser } = useAuth();
  const [mode, setMode] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [details, setDetails] = useState("");
  const [savedCases, setSavedCases] = useState([]);
  const [selectedCites, setSelectedCites] = useState(new Set());
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/assistant/history").then((r) => {
      if (r.data?.length) setMessages(r.data.map((m) => ({ role: m.role, content: m.content })));
    }).catch(() => {});
    api.get("/bookmarks").then((r) => setSavedCases(r.data.filter((b) => b.kind === "case"))).catch(() => {});
  }, []);

  const toggleCite = (id) => {
    setSelectedCites((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: userMsg }, { role: "assistant", content: "" }]);
    setLoading(true);
    try {
      await streamPost("/assistant/chat", { message: userMsg }, (chunk) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      });
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, something went wrong. Please try again." };
        return copy;
      });
    } finally {
      setLoading(false);
      refreshUser();
    }
  };

  const draft = async () => {
    if (!details.trim() || loading) return;
    setMessages((m) => [...m, { role: "user", content: `Draft a ${docType}:\n${details}` }, { role: "assistant", content: "" }]);
    setLoading(true);
    setMode("chat");
    try {
      await streamPost("/assistant/draft", { doc_type: docType, details, bookmark_ids: Array.from(selectedCites) }, (chunk) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      });
      setDetails("");
      setSelectedCites(new Set());
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, drafting failed. Please try again." };
        return copy;
      });
    } finally {
      setLoading(false);
      refreshUser();
    }
  };

  const copyMessage = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Copied"); } catch { toast.error("Copy failed"); }
      document.body.removeChild(ta);
    }
  };

  const downloadMessage = (text, i) => {
    const blob = new Blob([text], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `redemption-reply-${i + 1}.md`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded");
  };

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <Bot className="w-7 h-7 text-primary" />
          <h1 className="font-serif text-4xl">AI Legal Assistant</h1>
          <Link to="/credits" data-testid="assistant-credits" className="ml-auto flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors border border-border px-3 py-1.5 rounded-sm">
            <Sparkles className="w-3.5 h-3.5 text-primary" /> {user?.ai_credits ?? 0} credits
          </Link>
        </div>
        <p className="text-muted-foreground mb-8 text-sm">Ask questions about the material or draft documents. Powered by Claude Sonnet 4.6. Educational use only — not legal advice.</p>

        <div className="flex gap-2 mb-6">
          <button onClick={() => setMode("chat")} data-testid="mode-chat" className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-mono border transition-colors ${mode === "chat" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary"}`}>
            <MessageSquare className="w-4 h-4" /> Chat
          </button>
          <button onClick={() => setMode("draft")} data-testid="mode-draft" className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-mono border transition-colors ${mode === "draft" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary"}`}>
            <FileSignature className="w-4 h-4" /> Draft a document
          </button>
        </div>

        <div className="border border-border bg-card">
          <div ref={scrollRef} className="h-[50vh] overflow-y-auto p-6 space-y-6" data-testid="chat-window">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                <Bot className="w-10 h-10 mb-4 text-primary/60" />
                <p className="max-w-sm text-sm">Ask about trusts, the Acts of Congress, the "Person" treatise, citizenship, or consumer law — or switch to "Draft a document" to generate a template. Hover any reply to copy or download it.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-8 h-8 shrink-0 flex items-center justify-center border ${m.role === "user" ? "border-primary bg-primary/10" : "border-border bg-muted"}`}>
                  {m.role === "user" ? <User className="w-4 h-4 text-primary" /> : <Bot className="w-4 h-4 text-primary" />}
                </div>
                <div className={`group max-w-[80%] text-sm leading-relaxed px-4 py-3 rounded-sm relative ${m.role === "user" ? "bg-primary/10 border border-primary/30 whitespace-pre-wrap" : "bg-muted border border-border"}`}>
                  {m.content ? (
                    m.role === "assistant" ? (
                      <div className="prose-chat">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                      </div>
                    ) : m.content
                  ) : (loading && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : "")}
                  {m.content && (
                    <div className="absolute -top-2.5 -right-2.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => copyMessage(m.content)} data-testid={`copy-message-${i}`}
                        className="bg-card border border-border rounded-full p-1.5 text-muted-foreground hover:text-primary" title="Copy this reply">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => downloadMessage(m.content, i)} data-testid={`download-message-${i}`}
                        className="bg-card border border-border rounded-full p-1.5 text-muted-foreground hover:text-primary" title="Download this reply">
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {mode === "chat" ? (
            <div className="border-t border-border p-4 flex gap-3">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask a question…"
                className="flex-1 bg-muted border border-input px-3 py-2.5 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors text-sm"
              />
              <button onClick={send} disabled={loading} data-testid="chat-send" className="bg-primary text-primary-foreground px-5 rounded-sm flex items-center gap-2 hover:-translate-y-[1px] transition-transform disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          ) : (
            <div className="border-t border-border p-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                {DOC_TYPES.map((d) => (
                  <button key={d} onClick={() => setDocType(d)} data-testid={`doctype-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`} className={`px-3 py-1.5 text-xs font-mono border transition-colors ${docType === d ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>
                    {d}
                  </button>
                ))}
              </div>
              <textarea
                data-testid="draft-details"
                rows={3}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder={`Describe what the ${docType} should contain — parties, purpose, terms, dates…`}
                className="w-full bg-muted border border-input px-3 py-2.5 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors text-sm"
              />
              {savedCases.length > 0 && (
                <div className="border border-border rounded-sm p-3" data-testid="cite-cases">
                  <div className="text-[10px] uppercase tracking-widest font-mono text-primary mb-2">Cite your saved cases</div>
                  <div className="max-h-32 overflow-y-auto space-y-1.5">
                    {savedCases.map((c) => (
                      <label key={c.id} className="flex items-start gap-2 text-sm cursor-pointer hover:text-primary">
                        <input type="checkbox" data-testid={`cite-${c.id}`} checked={selectedCites.has(c.id)} onChange={() => toggleCite(c.id)} className="mt-1 accent-current" />
                        <span><span className="font-serif">{c.title}</span> {c.citation && <span className="text-xs font-mono text-muted-foreground">— {c.citation}</span>}</span>
                      </label>
                    ))}
                  </div>
                  {selectedCites.size > 0 && <div className="text-xs text-muted-foreground mt-2">{selectedCites.size} case(s) will be woven into the draft.</div>}
                </div>
              )}
              <button onClick={draft} disabled={loading} data-testid="draft-generate" className="bg-primary text-primary-foreground px-5 py-2.5 rounded-sm text-sm flex items-center gap-2 hover:-translate-y-[1px] transition-transform disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Generate draft
              </button>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
